/// <amd-module name="VSSUI/Components/Toast/Toast" />
import * as React from "react";
import { ICancelablePromise, makeCancelable } from "VSS/Core/Util/Promise";
import { Button } from "VSSUI/Button";
import { Callout, ContentJustification, ContentLocation } from "VSSUI/Callout";
import { IToastProps } from "VSSUI/Components/Toast/Toast.Props";
import { Measure } from "VSSUI/Measure";
import { css, getSafeId, KeyCode } from "VSSUI/Util";

export interface IToastState {
    callToActionWidth?: number;
    fadingOut: boolean;
}

export class Toast extends React.Component<IToastProps, IToastState> {
    private fadeOutPromise: ICancelablePromise<void>;
    private callToActionRef = React.createRef<Button>();

    constructor(props: IToastProps) {
        super(props);

        this.state = { fadingOut: false };
    }

    public fadeOut(): ICancelablePromise<void> {
        this.fadeOutPromise && this.fadeOutPromise.cancel();
        const basePromise = new Promise<void>(resolve => {
            this.setState({ fadingOut: true });

            setTimeout(() => {
                resolve();
            }, 500);
        });

        this.fadeOutPromise = makeCancelable(basePromise);
        return this.fadeOutPromise;
    }

    public render() {
        const { message, callToAction, className } = this.props;
        const { callToActionWidth, fadingOut } = this.state;

        // Set up class names based on layout
        const renderAsSingleLine = !callToAction || !callToActionWidth || callToActionWidth <= 120;
        const flexLayoutClass = renderAsSingleLine ? "one-line flex-row" : "multi-line flex-column";
        const messageClass = renderAsSingleLine ? "flex-grow" : undefined;

        const fadeOutClass = fadingOut ? "fade-out" : undefined;

        return (
            <Callout
                className={css(className, "bolt-toast no-events")}
                contentClassName={css("bolt-toast-content", flexLayoutClass, fadeOutClass)}
                contentJustification={ContentJustification.Center}
                contentLocation={ContentLocation.End}
            >
                <span className={css("bolt-toast-message flex-shrink", messageClass)} id={getSafeId("toast-message")} role="alert">
                    {message}
                </span>
                {callToAction && (
                    <>
                        <span className={"bolt-toast-separator flex-noshrink"} />
                        <Measure onMeasure={this.onMeasureCallToAction}>
                            <div className="bolt-toast-call-to-action-container">
                                <Button
                                    ariaDescribedBy={"toast-message"}
                                    className={"bolt-toast-call-to-action flex-noshrink"}
                                    ref={this.callToActionRef}
                                    subtle={true}
                                    onClick={this.onCallToActionClick}
                                >
                                    {callToAction}
                                </Button>
                            </div>
                        </Measure>
                    </>
                )}
            </Callout>
        );
    }

    public componentDidMount() {
        document.addEventListener("keydown", this.onKeyboardShortcut);
    }

    public componentWillUnmount() {
        this.fadeOutPromise && this.fadeOutPromise.cancel();
        document.removeEventListener("keydown", this.onKeyboardShortcut);
    }

    private onCallToActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (this.props.onCallToActionClick) {
            this.props.onCallToActionClick(event);
        }
    };

    private onKeyboardShortcut = (event: KeyboardEvent) => {
        if (this.callToActionRef.current && !this.state.fadingOut && event.ctrlKey && event.altKey && event.which === KeyCode.t) {
            this.callToActionRef.current.focus();
        }
    };

    // Be careful to avoid layout thrashing here; the call to action must NOT change width on re-render
    private onMeasureCallToAction = (newWidth: number, newHeight: number) => {
        this.setState({ callToActionWidth: newWidth });
    };
}
